import { Col, Form, Row } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { OVERCHARGE_CLAIMS_FILTERS_INITIAL_DATA } from '../../../../contants/ClaimsEnums';
import {
  getOverChargeClamsListInProgress,
  setClaimsOverChargeFilters,
  setOverchargeList,
} from '../../../../redux/Claims/OverCharge/OverChargeActions';
import { valueToNumber, valueToString } from '../../../../utilz';
import { convertDateUserLocalToUTCToEST } from '../../../../utilz/dates';
import { routeFileSelector } from '../../../../utilz/i18n';
import Button from '../../../components/Buttons/Button';
import FormDatePicker from '../../../components/Inputs/FormDatePicker';
import FormInput from '../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../components/Labels/FormLabel';
import TransportationProviderSelector from '../../../components/Selectors/AsynchSearch/TransportationProviderSelector';
import OverChargeClaimStatusSelector from '../../../components/Selectors/OverChargeClaimStatusSelector';
import ToggleSwitch from '../../../components/Switch/ToggleSwitch';

const OverChargeClaimsFilters = () => {
  const dispatch = useDispatch();
  const { claimsFilters } = useSelector((state) => state.overChargeClaims);
  const [form] = Form.useForm();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const handleSubmit = (data) => {
    const payload = {
      ...claimsFilters,
      fbocKey: valueToString(data?.fbocKey),
      carrierKey: valueToNumber(data?.carrierKey) ? valueToNumber(data?.carrierKey) : undefined,
      freightBillNo: valueToString(data?.freightBillNo),
      invoiceNo: valueToString(data?.invoiceNo),
      packageNo: valueToString(data?.packageNo),
      daysOld: valueToNumber(data?.daysOld),
      pageSize: 10,
      pageNo: 1,
      byAge: data.byAge,
      StatusCodes: data.StatusCodes,
      StartDate: convertDateUserLocalToUTCToEST(data.StartDate),
      EndDate: convertDateUserLocalToUTCToEST(data.EndDate),
    };
    dispatch(setOverchargeList({ data: [], progressing: true }));
    dispatch(setClaimsOverChargeFilters(payload));
    dispatch(getOverChargeClamsListInProgress(payload));
  };

  const byAge = Form.useWatch('byAge', form);
  const daysOld = Form.useWatch('daysOld', form);
  const StartDate = Form.useWatch('StartDate', form);
  const EndDate = Form.useWatch('EndDate', form);

  useEffect(() => {
    form.setFieldValue('daysOld', Number.parseInt(daysOld));
  }, [daysOld]);

  useEffect(() => {
    if (claimsFilters) {
      form.setFieldsValue(claimsFilters);
    }
  }, [claimsFilters]);

  const handleResetButton = () => form.setFieldsValue(OVERCHARGE_CLAIMS_FILTERS_INITIAL_DATA);

  return (
    <div className="h-100 overflow-hidden d-flex flex-direction-column">
      <div className="flex-1 px-15 custom-scroll-bar">
        <Form form={form} onFinish={handleSubmit} initialValues={claimsFilters}>
          <Row className="flex-direction-column flex-nowrap">
            <Col className="mb-15">
              <Row gutter={5} className="flex-nowrap">
                <Col>
                  <FormLabel>{t('translation:claims.byAge')}</FormLabel>
                </Col>
                <Col>
                  <ToggleSwitch
                    checked={byAge}
                    onClick={(val) => form.setFieldValue('byAge', val)}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                  />
                </Col>
              </Row>
              <HiddenFormInput name="byAge" />
            </Col>
            {byAge && (
              <Col>
                <FormInput
                  label={t('translation:claims.daysOld')}
                  placeholder={t('translation:claims.pleaseEnterDaysOld')}
                  name="daysOld"
                  editMode
                  margin="mb-15"
                />
              </Col>
            )}

            <Col>
              <FormInput
                label={t('translation:claims.claimsNumber')}
                placeholder={t('translation:claims.pleaseEnterClaimsNumber')}
                name="fbocKey"
                editMode
                margin="mb-15"
              />
            </Col>
            <Col className="mb-15">
              <TransportationProviderSelector name="carrierKey" form={form} showPopupFilter />
            </Col>
            <Col>
              <FormInput
                label={t('translation:claims.freightBillProNumber')}
                placeholder={t('translation:claims.pleaseEnterFreightBillProNumber')}
                name="freightBillNo"
                editMode
                margin="mb-15"
              />
            </Col>
            <Col>
              <FormInput
                label={t('translation:default.fields.invoiceNumber')}
                placeholder={t('translation:claims.pleaseEnterInvoiceNumber')}
                name="invoiceNo"
                editMode
                margin="mb-15"
                maxLength={25}
              />
            </Col>
            <Col>
              <FormInput
                label={t('translation:default.fields.packageNumber')}
                placeholder={t('translation:claims.pleaseEnterPackageNumber')}
                name="packageNo"
                editMode
                margin="mb-15"
              />
            </Col>
            <Col>
              <OverChargeClaimStatusSelector
                editMode
                label={t('translation:default.fields.status.label')}
                name="StatusCodes"
                mode="multiple"
              />
            </Col>
            <Col sm={24}>
              <FormLabel>Filed On</FormLabel>
              <Row className="mt-3 align-items-center" gutter={5}>
                <Col sm={6} className="text-right">
                  <FormLabel>{t('translation:default.fields.from.label')}</FormLabel>
                </Col>
                <Col sm={18}>
                  <FormDatePicker
                    name="StartDate"
                    placeholder={t('translation:default.fields.createdOn.placeholder')}
                    margin="mb-0"
                    disableFuture
                    disabledTargetDate={EndDate}
                  />
                </Col>
              </Row>
              <Row className="mt-6 align-items-center" gutter={5}>
                <Col sm={6} className="text-right">
                  <FormLabel>{t('translation:default.fields.to.label')} :</FormLabel>
                </Col>
                <Col sm={18}>
                  <FormDatePicker
                    name="EndDate"
                    placeholder={t('translation:default.fields.createdOn.placeholder')}
                    margin="mb-0"
                    disableFuture={false}
                    disabledTargetDate={StartDate}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
      <Row className="px-15" gutter={15}>
        <Col sm={24} md={12}>
          <Button fullWidth type="default" onClick={handleResetButton} buttonType="button" id="shipment-filter-clear">
            {t('translation:default.buttons.clear')}
          </Button>
        </Col>
        <Col sm={24} md={12}>
          <Button
            fullWidth
            type="primary"
            onClick={() => form.submit()}
            buttonType="submit"
            id="shipment-filter-search"
          >
            {t('translation:default.buttons.search')}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default OverChargeClaimsFilters;
